
import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { merchandiseProducts } from '../../mocks/products';

export default function ApparelPage() {
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [cart, setCart] = useState<Array<{ id: number; quantity: number }>>([]);

  const categories = [
    { id: 'all', label: 'All Apparel', icon: 'ri-t-shirt-line' },
    { id: 'tees', label: 'T-Shirts', icon: 'ri-t-shirt-line' },
    { id: 'hoodies', label: 'Hoodies', icon: 'ri-hoodie-line' },
    { id: 'shorts', label: 'Shorts', icon: 'ri-scissors-cut-line' },
    { id: 'sweats', label: 'Sweatpants', icon: 'ri-walk-line' },
    { id: 'accessories', label: 'Accessories', icon: 'ri-hat-line' },
  ];

  const filteredProducts =
    activeFilter === 'all'
      ? merchandiseProducts
      : activeFilter === 'tees'
      ? merchandiseProducts.filter((p) => p.name.toLowerCase().includes('tee'))
      : activeFilter === 'hoodies'
      ? merchandiseProducts.filter(
          (p) =>
            p.name.toLowerCase().includes('hoodie') ||
            p.name.toLowerCase().includes('crewneck') ||
            p.name.toLowerCase().includes('sweatshirt')
        )
      : activeFilter === 'shorts'
      ? merchandiseProducts.filter((p) => p.name.toLowerCase().includes('shorts'))
      : activeFilter === 'sweats'
      ? merchandiseProducts.filter(
          (p) =>
            p.name.toLowerCase().includes('jogger') ||
            p.name.toLowerCase().includes('sweats')
        )
      : merchandiseProducts.filter((p) => p.category === 'Accessories');

  const addToCart = (productId: number) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.id === productId);
      if (existing) {
        return prev.map((item) =>
          item.id === productId ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, { id: productId, quantity: 1 }];
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/bb5a79371dafdabca55f4df2b8e05184.jpg"
            alt="Sentinel Brands apparel collection"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="font-display font-bold text-5xl md:text-6xl text-white mb-6">
              Sentinel <span className="text-electric-blue">Brands</span> Apparel
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto mb-8">
              Premium athletic wear designed for performance and style — all under $55
            </p>
            <Link
              to="/shop"
              className="px-8 py-3 bg-white/10 border border-white/30 text-white rounded-lg text-sm font-semibold hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
            >
              Shop Everything
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="py-12 bg-white border-b border-gray-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-3 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveFilter(cat.id)}
                className={`px-5 py-3 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                  activeFilter === cat.id
                    ? 'bg-electric-blue text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className={cat.icon}></i>
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-gray-50 to-baby-blue/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" data-product-shop>
            {filteredProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.04 }}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all group"
              >
                <div className="relative h-72 bg-gradient-to-br from-baby-blue/20 to-white overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    title={`${product.name} - Sentinel Brands`}
                    className="w-full h-full object-contain p-6 group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 px-3 py-1 bg-electric-blue text-white text-xs font-semibold rounded-full">
                    {product.category}
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-display font-bold text-lg text-navy mb-1">{product.name}</h3>
                  <p className="text-xs text-gray-600 mb-3 line-clamp-2">{product.description}</p>
                  {'sizes' in product && (
                    <div className="mb-3">
                      <div className="flex gap-1.5 flex-wrap">
                        {product.sizes.map((size: string) => (
                          <span
                            key={size}
                            className="w-9 h-9 flex items-center justify-center border border-gray-300 rounded-md text-xs font-medium hover:border-electric-blue hover:text-electric-blue transition-colors cursor-pointer"
                          >
                            {size}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-xl font-bold text-navy">${product.price}</span>
                    <button
                      onClick={() => addToCart(product.id)}
                      className="px-5 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Add to Cart
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {filteredProducts.length === 0 && (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg">No products found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {cart.length > 0 && (
        <div className="fixed bottom-8 right-8 z-50">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="bg-navy text-white px-6 py-4 rounded-full shadow-2xl flex items-center gap-3 cursor-pointer hover:bg-navy-dark transition-colors"
          >
            <div className="w-10 h-10 flex items-center justify-center">
              <i className="ri-shopping-cart-line text-2xl"></i>
            </div>
            <div>
              <div className="text-sm font-semibold">
                {cart.reduce((sum, item) => sum + item.quantity, 0)} items
              </div>
              <div className="text-xs text-white/70">View Cart</div>
            </div>
          </motion.div>
        </div>
      )}

      <Footer />
    </div>
  );
}
